import { useEffect, useRef } from "react";
import anime from "animejs";

interface ProgressRingProps {
  progress: number; // 0-1
  errorProgress: number; // 0-1
  completed: number;
  total: number;
  size?: number;
  isComplete: boolean;
}

export function ProgressRing({
  progress,
  errorProgress,
  completed,
  total,
  size = 80,
  isComplete,
}: ProgressRingProps) {
  const progressRef = useRef<SVGCircleElement>(null);
  const errorRef = useRef<SVGCircleElement>(null);
  const glowRef = useRef<SVGCircleElement>(null);
  const prevProgressRef = useRef(0);
  const completeCelebrated = useRef(false);

  const strokeWidth = 4;
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Animate progress changes
  useEffect(() => {
    const el = progressRef.current;
    if (!el) return;

    const fromOffset = circumference * (1 - prevProgressRef.current);
    const toOffset = circumference * (1 - progress);
    prevProgressRef.current = progress;

    anime({
      targets: el,
      strokeDashoffset: [fromOffset, toOffset],
      easing: "easeOutQuad",
      duration: 500,
    });
  }, [progress, circumference]);

  // Animate error arc
  useEffect(() => {
    const el = errorRef.current;
    if (!el) return;

    const offset = circumference * (1 - errorProgress);
    anime({
      targets: el,
      strokeDashoffset: offset,
      easing: "easeOutQuad",
      duration: 500,
    });
  }, [errorProgress, circumference]);

  // Complete celebration: glow pulse
  useEffect(() => {
    if (!isComplete || completeCelebrated.current) return;
    completeCelebrated.current = true;

    const glow = glowRef.current;
    if (!glow) return;

    anime({
      targets: glow,
      opacity: [0, 0.6, 0],
      easing: "easeOutCubic",
      duration: 1200,
    });
  }, [isComplete]);

  // Reset celebration flag when not complete
  useEffect(() => {
    if (!isComplete) completeCelebrated.current = false;
  }, [isComplete]);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
    >
      {/* Glow filter */}
      <defs>
        <filter id="ring-glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Background track */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="var(--tr-border)"
        strokeWidth={strokeWidth}
      />

      {/* Glow layer (visible on completion) */}
      <circle
        ref={glowRef}
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={isComplete ? "var(--tr-ok)" : "var(--tr-accent)"}
        strokeWidth={strokeWidth + 4}
        strokeDasharray={circumference}
        strokeDashoffset={0}
        strokeLinecap="round"
        opacity={0}
        filter="url(#ring-glow)"
        transform={`rotate(-90 ${center} ${center})`}
      />

      {/* Error arc (from the end going backwards) */}
      {errorProgress > 0 && (
        <circle
          ref={errorRef}
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--tr-bad)"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference}
          strokeLinecap="round"
          opacity={0.6}
          transform={`rotate(${-90 + 360 * progress} ${center} ${center})`}
        />
      )}

      {/* Progress arc (accent -> ok on complete) */}
      <circle
        ref={progressRef}
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={isComplete ? "var(--tr-ok)" : "var(--tr-accent)"}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={circumference}
        strokeLinecap="round"
        transform={`rotate(-90 ${center} ${center})`}
        className="transition-[stroke] duration-500"
      />

      {/* Center text: completed / total */}
      <text
        x={center}
        y={center - 6}
        textAnchor="middle"
        dominantBaseline="middle"
        className="font-mono text-lg font-semibold"
        fill={isComplete ? "var(--tr-ok)" : "var(--tr-text)"}
      >
        {completed}
      </text>
      <line
        x1={center - 10}
        y1={center + 4}
        x2={center + 10}
        y2={center + 4}
        stroke="var(--tr-border-3)"
        strokeWidth="1"
      />
      <text
        x={center}
        y={center + 16}
        textAnchor="middle"
        dominantBaseline="middle"
        className="font-mono text-[10px]"
        fill="var(--tr-text-3)"
      >
        {total}
      </text>
    </svg>
  );
}
